// Хост уведомлений. Стоит один раз на уровне App.
// Source: docs/specs/design-revision/a-notifications.md §2.2, §2.5, §2.6.

import { useToast, TOAST_MS, type ToastVariant } from '@/hooks/use-toast';
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastIcon,
  ToastProgress,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from '@/components/ui/toast';
import { pluralize } from '@/lib/utils';

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    // swipeDirection="down" — совпадает с направлением появления. По умолчанию
    // Radix даёт "right", и на плашке, прилетевшей снизу, свайп не срабатывал.
    // duration здесь только значение по умолчанию: каждая плашка приносит своё
    // из resolveToastPolicy, иначе незакрываемых ошибок не получится.
    // swipeThreshold заведомо недостижим: встроенный свайп Radix отключён,
    // потому что направление у него ровно одно, а нужны три — вниз, влево
    // и вправо. Жест ведём сами, см. useSwipeDismiss в toast.tsx.
    <ToastProvider swipeDirection="down" swipeThreshold={100000} duration={4000}>
      {toasts.map(({
        id, title, description, action, variant,
        occurrences, duration, announce, timerNonce,
        // dedupeKey и createdAt — наши поля, в DOM им делать нечего.
        dedupeKey: _dedupeKey, createdAt: _createdAt,
        ...props
      }) => (
        <Toast
          // timerNonce в ключе: при повторе Radix перемонтирует плашку и
          // начинает отсчёт заново. Так «повтор перезапускает таймер» работает
          // без ручного управления таймерами.
          key={`${id}:${timerNonce}`}
          variant={variant}
          duration={duration}
          // Именно type создаёт assertive live region у Radix. Произвольный
          // aria-live на Root не сработал бы: регионом управляет сам Radix.
          type={announce}
          // БЕЗ ЭТОГО ПЛАШКА НЕ ЗАКРЫВАЕТСЯ НИКАК. `open` приходит из нашего
          // состояния, то есть Radix здесь управляемый: истёкший таймер, свайп
          // и крестик — все три пути ведут в onOpenChange, и если его не
          // передать, Radix просто некому сообщить о закрытии. Ровно это и было
          // на сборке от 29.09: плашка «Анализ удалён» висела вечно и не
          // реагировала ни на что.
          onOpenChange={(isOpen) => {
            if (!isOpen) dismiss(id);
          }}
          onSwipeDismiss={() => dismiss(id)}
          {...props}
        >
          <ToastIcon variant={(variant ?? 'info') as ToastVariant} />

          <div className="min-w-0 flex-1 pt-0.5">
            {title && (
              <ToastTitle>
                {title}
                {occurrences > 1 && (
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    ×{occurrences}
                  </span>
                )}
              </ToastTitle>
            )}
            {description && <ToastDescription>{description}</ToastDescription>}
            {action}
            {/* Скринридеру повтор проговаривается один раз числом, а не N раз
                подряд одним и тем же сообщением. */}
            {occurrences > 1 && (
              <span className="sr-only">
                {' '}
                Повторилось {occurrences}{' '}
                {pluralize(occurrences, ['раз', 'раза', 'раз'])}
              </span>
            )}
          </div>

          <ToastClose aria-label="Закрыть уведомление" />

          {/* Полоска обратного отсчёта — только там, где отсчитывать есть что.
              Ключ с timerNonce перемонтирует плашку при повторе, поэтому
              анимация полоски начинается заново вместе с таймером. */}
          {duration !== TOAST_MS.persistent && (
            <ToastProgress duration={duration} tone={(variant ?? 'info') as ToastVariant} />
          )}
        </Toast>
      ))}
      <ToastViewport />
    </ToastProvider>
  );
}
